/*
 * BuenaVida global form hardening + mailto de-exposure.
 *
 * Three jobs, one small file loaded once site-wide:
 *   1. Enforce the bot guard rendered by snippets/bv-form-guard.liquid (honeypot + time-trap).
 *   2. Reassemble the addresses rendered by snippets/bv-mailto.liquid, so no plaintext email
 *      is ever present in the served HTML for a harvester to scrape.
 *   3. Suppress a newsletter success message that belongs to a DIFFERENT form on the same page
 *      (the popup/footer cross-fire — see assets/bv-homepage-popup.js).
 *
 * Everything here fails OPEN. With JavaScript disabled: nothing is stamped so nothing is
 * blocked, and the mailto links navigate to the contact page instead. A human always gets through.
 */
(function () {
  'use strict';

  /* ── 1. Bot guard ────────────────────────────────────────────────────────
   * CAPTURE phase is required: a blocked submit must never reach a section's own submit
   * handler (the popup's, for one, would otherwise record that a submit is pending).
   */
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;

      var guard = form.querySelector('[data-bv-guard]');
      if (!guard) return;

      var pot = guard.querySelector('input[name="bv_confirm"]');
      var stamp = guard.querySelector('[data-bv-guard-ts]');
      var min = (parseFloat(guard.getAttribute('data-bv-guard-min')) || 2) * 1000;

      // Honeypot. A human cannot reach this field (off-screen, tabindex -1, aria-hidden,
      // autocomplete off), so a value here is a bot. Drop silently — never explain to a bot.
      if (pot && pot.value.trim() !== '') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Time-trap. Unlike the honeypot this CAN false-positive on a fast human (paste and click),
      // so it re-stamps: the second click always goes through.
      if (stamp && stamp.value) {
        var elapsed = Date.now() - parseInt(stamp.value, 10);
        if (elapsed >= 0 && elapsed < min) {
          e.preventDefault();
          e.stopPropagation();
          stamp.value = String(Date.now() - min);
        }
      }
    },
    true
  );

  function stampGuards(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(root.querySelectorAll('[data-bv-guard-ts]'), function (el) {
      if (!el.value) el.value = String(Date.now());
    });
  }

  /* ── 2. Mailto reassembly ────────────────────────────────────────────── */

  function reverse(value) {
    return String(value || '').split('').reverse().join('');
  }

  /* Defer to idle if available, else a plain timeout. requestIdleCallback takes an OPTIONS object
   * as its 2nd argument, not a delay — passing a number throws and (before this was fixed) aborted
   * the wiring loop, leaving every mailto link after the first one dead. */
  function whenIdle(fn) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 1500 });
    } else {
      window.setTimeout(fn, 1200);
    }
  }

  function wireMailto(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(root.querySelectorAll('[data-bv-mailto]'), function (link) {
      if (link.dataset.bvMailtoWired === '1') return;
      link.dataset.bvMailtoWired = '1';

      var built = false;
      function build() {
        if (built) return;
        built = true;
        var address =
          reverse(link.getAttribute('data-bv-mailto-local')) +
          '@' +
          reverse(link.getAttribute('data-bv-mailto-host'));
        link.setAttribute('href', 'mailto:' + address);
        if (link.getAttribute('data-bv-mailto-reveal') === 'true') {
          var slot = link.querySelector('.bv-mailto__text') || link;
          slot.textContent = address;
        }
      }

      // One malformed link must never abort the loop and leave the rest of the page's addresses
      // unwired — that is exactly how this broke the first time.
      try {
        link.addEventListener('pointerenter', build);
        link.addEventListener('focus', build);
        link.addEventListener('touchstart', build, { passive: true });
        link.addEventListener('click', function (e) {
          if (built) return;
          e.preventDefault();
          build();
          window.location.href = link.getAttribute('href');
        });

        // Reveal-mode links display the address as their text, so they must resolve without
        // interaction — but late enough that a naive scraper reading the initial HTML misses it.
        if (link.getAttribute('data-bv-mailto-reveal') === 'true') whenIdle(build);
      } catch (e) {
        /* keep wiring the remaining links */
      }
    });
  }

  /* ── 3. Form ownership (the popup/footer cross-fire fix) ─────────────────
   * `form.posted_successfully?` is page-scoped per form TYPE, so every {% form 'customer' %} on
   * the page renders its success state when ANY of them was submitted. That is why subscribing in
   * the footer used to force the homepage popup open with the discount code, and vice versa.
   *
   * On submit we record which form it was; on the next load each surface checks that record
   * before celebrating. sessionStorage rather than a return_to URL token on purpose: a custom
   * return_to appears to suppress posted_successfully? on the landing page, which would silently
   * remove the footer's server-rendered confirmation for visitors with no JavaScript.
   *
   * Fail-open: with no JS nothing is recorded, nothing is hidden, and the server-rendered footer
   * message stands — which is true either way, the visitor did subscribe.
   */
  var OWNER_KEY = 'bv_form_owner';

  function recordOwner(form) {
    var owner = form.getAttribute('data-bv-owner');
    if (!owner) return;
    try {
      sessionStorage.setItem(OWNER_KEY, owner);
    } catch (e) {
      /* private mode — falls back to showing the server-rendered state */
    }
  }

  function readOwner() {
    try {
      return sessionStorage.getItem(OWNER_KEY);
    } catch (e) {
      return null;
    }
  }

  function filterForeignSuccess() {
    var owner = readOwner();
    if (!owner) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-bv-form-owner]'), function (el) {
      if (el.getAttribute('data-bv-form-owner') !== owner) el.hidden = true;
    });
  }

  // Record the owner in the BUBBLE phase: by then the capture-phase guard above has had its say,
  // so a bot-blocked submit never claims ownership.
  document.addEventListener('submit', function (e) {
    if (e.defaultPrevented) return;
    if (e.target && e.target.tagName === 'FORM') recordOwner(e.target);
  });

  function init(scope) {
    stampGuards(scope);
    wireMailto(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
      filterForeignSuccess();
    });
  } else {
    init(document);
    filterForeignSuccess();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
